import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Navbar } from '../components/Navbar'
import { Button } from '../components/ui/Button'
import { Card, CardContent } from '../components/ui/Card'
import {MapPin, ArrowLeft, Navigation, Droplet, Users, AlertCircle, Building2, Phone, Clock, ExternalLink, MessageCircleDashed as MessageCircle} from 'lucide-react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'
import { LeafletMapComponent } from '../components/LeafletMapComponent'

interface Hemocenter {
  _id: string
  name: string
  type: string
  street: string
  number: string
  neighborhood: string
  city: string
  state: string
  zipCode: string
  latitude: number
  longitude: number
  phone: string
  whatsapp?: string
  website?: string
  hours: string
  notes?: string
  distance?: number
}

export function MapView() {
  const navigate = useNavigate()
  const [requests, setRequests] = useState<any[]>([])
  const [donors, setDonors] = useState<any[]>([])
  const [hemocenters, setHemocenters] = useState<Hemocenter[]>([])
  const [loading, setLoading] = useState(true)
  const [userLocation, setUserLocation] = useState<{lat: number, lng: number} | null>(null)
  const [userCity, setUserCity] = useState<string | null>(null)

  useEffect(() => {
    // Get user location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          })
        },
        (error) => {
          console.error('Error getting location:', error)
          toast.error('Não foi possível obter sua localização')
        }
      )
    }

    fetchUserProfile()
    fetchData()
  }, [])

  const fetchUserProfile = async () => {
    try {
      if (lumi.auth.user) {
        const profileData = await lumi.entities.profiles.list({
          filter: { userId: lumi.auth.user.userId },
          limit: 1
        })
        if (profileData.list.length > 0) {
          setUserCity(profileData.list[0].city)
        }
      }
    } catch (error) {
      console.error('❌ Failed to fetch user profile:', error)
    }
  }

  const fetchData = async () => {
    try {
      console.log('🗺️ Fetching map data...')
      const [requestsData, donorsData, hemocentersData] = await Promise.all([
        lumi.entities.requests.list({
          filter: { status: 'active' },
          limit: 15 // Otimizado para velocidade máxima
        }),
        lumi.entities.profiles.list({
          filter: { isDonor: true },
          limit: 20 // Otimizado para velocidade máxima
        }),
        lumi.entities.hemocenters.list({ limit: 20 }) // Otimizado para velocidade máxima
      ])
      
      console.log('✅ Data fetched:', {
        requests: requestsData.list.length,
        donors: donorsData.list.length,
        hemocenters: hemocentersData.list.length
      })
      
      setRequests(requestsData.list)
      setDonors(donorsData.list)
      setHemocenters(hemocentersData.list)
    } catch (error) {
      console.error('❌ Failed to fetch data:', error)
      toast.error('Erro ao carregar dados')
    } finally {
      setLoading(false)
    }
  }

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371 // Earth radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180
    const dLon = (lon2 - lon1) * Math.PI / 180
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
    return R * c
  }

  const getNearbyRequests = useMemo(() => {
    // Filter by user's city if available
    let filteredRequests = requests
    if (userCity) {
      filteredRequests = requests.filter(req => req.city === userCity)
    }
    
    if (!userLocation) return filteredRequests.slice(0, 8)
    
    return filteredRequests
      .map(req => ({
        ...req,
        distance: req.latitude && req.longitude 
          ? calculateDistance(userLocation.lat, userLocation.lng, req.latitude, req.longitude)
          : 999
      }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 8)
  }, [requests, userCity, userLocation])

  const getFilteredHemocenters = useMemo(() => {
    // Filter by user's city if available
    if (userCity) {
      return hemocenters.filter(center => center.city === userCity).slice(0, 15)
    }
    return hemocenters.slice(0, 15)
  }, [hemocenters, userCity])

  const getNearbyDonors = (bloodType: string) => {
    return donors
      .filter(d => d.bloodType === bloodType)
      .slice(0, 5)
  }

  const getNearbyHemocenters = useMemo(() => {
    if (!userLocation) return hemocenters.slice(0, 5)
    
    return hemocenters
      .map(center => ({
        ...center,
        distance: calculateDistance(userLocation.lat, userLocation.lng, center.latitude, center.longitude)
      }))
      .sort((a, b) => (a.distance || 0) - (b.distance || 0))
      .slice(0, 5)
  }, [hemocenters, userLocation])

  const openGoogleMaps = (center: Hemocenter) => {
    const address = `${center.street}, ${center.number}, ${center.neighborhood}, ${center.city} - ${center.state}`
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank')
  }

  const openWhatsApp = (phone: string) => {
    const cleanPhone = phone.replace(/\D/g, '')
    window.open(`https://wa.me/55${cleanPhone}`, '_blank')
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="container mx-auto px-4 py-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center shadow-md">
              <MapPin className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-black text-gray-900 mb-2">Mapa de Doadores</h1>
          <p className="text-gray-600">
            Encontre pedidos, doadores e hemocentros próximos a você
          </p>
        </div>

        {/* Location Status */}
        {userLocation ? (
          <Card className="mb-6 bg-white border border-red-200">
            <CardContent className="pt-6">
              <div className="flex items-center space-x-3">
                <Navigation className="h-5 w-5 text-red-600" />
                <div>
                  <p className="font-bold text-gray-900">Localização ativada</p>
                  <p className="text-sm text-gray-700">Mostrando resultados próximos a você</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="mb-6 bg-yellow-50 border border-yellow-300">
            <CardContent className="pt-6">
              <div className="flex items-center space-x-3">
                <AlertCircle className="h-5 w-5 text-yellow-600" />
                <div>
                  <p className="font-bold text-gray-900">📍 Ative sua localização para encontrar doadores perto de você</p>
                  <p className="text-sm text-gray-700">Mostramos pedidos próximos quando você permite acesso à localização</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Google Maps Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
            <MapPin className="h-6 w-6 text-red-600 mr-2" />
            Visualização no Mapa
          </h2>
          <Card className="overflow-hidden">
            <CardContent className="p-0">
              <div style={{ minHeight: '500px', height: '60vh' }}>
                {!loading && (
                  <LeafletMapComponent
                    markers={[
                      ...getNearbyRequests.map(req => ({
                        id: `request-${req._id}`,
                        type: req.urgency === 'critical' ? 'pedido_critico' as const : 
                              req.urgency === 'high' ? 'pedido_urgente' as const : 
                              'pedido_normal' as const,
                        position: { 
                          lat: req.latitude || -21.764, 
                          lng: req.longitude || -43.350 
                        },
                        name: req.patientName,
                        bloodType: req.bloodType,
                        city: req.city,
                        state: req.state,
                        urgency: req.urgency,
                        hospital: req.hospital,
                        createdAt: new Date(req.createdAt).toLocaleDateString('pt-BR')
                      })),
                      ...getFilteredHemocenters.map(center => ({
                        id: `hemocentro-${center._id}`,
                        type: 'hemocentro' as const,
                        position: { lat: center.latitude, lng: center.longitude },
                        name: center.name,
                        city: center.city,
                        state: center.state,
                        phone: center.phone,
                        whatsapp: center.whatsapp,
                        hours: center.hours
                      }))
                    ]}
                    center={userLocation || (userCity ? { lat: -21.764, lng: -43.350 } : { lat: -14.235, lng: -51.925 })}
                    zoom={userLocation ? 12 : userCity ? 11 : 4}
                    userLocation={userLocation}
                  />
                )}
                {loading && (
                  <div className="flex items-center justify-center h-full bg-gray-100">
                    <div className="text-center">
                      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mb-4"></div>
                      <p className="text-gray-600">Carregando mapa...</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Nearby Requests */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
            <Droplet className="h-6 w-6 text-red-600 mr-2" />
            {userCity ? `Pedidos em ${userCity}` : 'Pedidos Próximos'}
          </h2>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i} className="h-40 animate-pulse bg-gray-100" />
              ))}
            </div>
          ) : getNearbyRequests.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {getNearbyRequests.map((req: any) => (
                <Card key={req._id} className="hover:shadow-lg transition-shadow border-l-4 border-l-red-500">
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 rounded-full bg-red-100 text-red-700 font-bold text-lg flex items-center justify-center">
                          {req.bloodType}
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900">{req.patientName}</h3>
                          <p className="text-sm text-gray-600">{req.hospital}</p>
                        </div>
                      </div>
                      {req.distance && req.distance < 999 && (
                        <div className="text-right">
                          <p className="text-sm font-bold text-red-600">{req.distance.toFixed(1)} km</p>
                          <p className="text-xs text-gray-500">de você</p>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center text-sm text-gray-600 mb-2">
                      <MapPin className="h-4 w-4 mr-1" />
                      {req.city}/{req.state}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        req.urgency === 'critical' ? 'bg-red-100 text-red-800' :
                        req.urgency === 'high' ? 'bg-orange-100 text-orange-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {req.urgency === 'critical' ? 'CRÍTICO' : req.urgency === 'high' ? 'URGENTE' : 'NORMAL'}
                      </span>
                      <Button 
                        size="sm"
                        onClick={() => navigate(`/solicitacao/${req._id}`)}
                      >
                        Ver Detalhes
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="pt-6 text-center py-12">
                <Droplet className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">Nenhum pedido próximo no momento</h3>
                <p className="text-gray-600">Quando alguém da sua região precisar, você será um dos primeiros a saber</p>
              </CardContent>
            </Card>
          )}
        </div>



        {/* Statistics by Location */}
        <Card className="bg-white border border-red-200">
          <CardContent className="pt-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Users className="h-5 w-5 text-red-600 mr-2" />
              Estatísticas da Rede
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg p-4 text-center border border-red-200">
                <p className="text-3xl font-black text-red-600">{requests.length}</p>
                <p className="text-xs text-gray-600 font-medium">Pedidos Ativos</p>
              </div>
              <div className="bg-white rounded-lg p-4 text-center border border-red-200">
                <p className="text-3xl font-black text-red-600">{donors.length}</p>
                <p className="text-xs text-gray-600 font-medium">Doadores</p>
              </div>
              <div className="bg-white rounded-lg p-4 text-center border border-red-200">
                <p className="text-3xl font-black text-red-600">{hemocenters.length}</p>
                <p className="text-xs text-gray-600 font-medium">Hemocentros</p>
              </div>
              <div className="bg-white rounded-lg p-4 text-center border border-red-200">
                <p className="text-3xl font-black text-red-600">
                  {requests.filter(r => r.urgency === 'critical').length}
                </p>
                <p className="text-xs text-gray-600 font-medium">Críticos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="mt-8 text-center">
          <Button 
            onClick={() => navigate('/solicitacoes')}
            className="w-full max-w-md h-14 text-lg font-bold bg-red-600 hover:bg-red-700 text-white"
          >
            Ver Todas as Solicitações
          </Button>
        </div>
      </div>
    </div>
  )
}
